import React from 'react';
import { Routes, Route, NavLink, Outlet, Navigate } from 'react-router-dom';
import { SidebarProvider, SidebarInset } from '@/components/ui/sidebar';
import AppSidebar from '@/components/layout/AppSidebar';
import Header from '@/components/layout/Header';
import AdminGuard from '@/components/admin/AdminGuard';
import { LayoutDashboard, Package, Layers, ListChecks, Wand2, Rocket, Table2 } from 'lucide-react';
import AdminOverview from '@/components/admin/AdminOverview';
import AdminListini from '@/components/admin/AdminListini';
import AdminStratigrafie from '@/components/admin/AdminStratigrafie';
import AdminEntitlements from '@/components/admin/AdminEntitlements';
import AdminReleases from '@/components/admin/AdminReleases';
import AdminReleaseDetail from '@/components/admin/AdminReleaseDetail';
import AdminCatalogo from '@/components/admin/AdminCatalogo';

/**
 * F26 — Portale admin DryConfig (gestione catalogo Store, listini, stratigrafie vendibili).
 *
 * Layout:
 *  - Header app + sidebar applicativa standard
 *  - Tab bar interna /admin con 4 sezioni
 *  - Outlet renderizza la sezione corrente
 *
 * Accesso: solo se l'utente è in public.app_admins (AdminGuard).
 */
const AdminTabs: { to: string; label: string; icon: React.ComponentType<{ className?: string }>; }[] = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard },
  { to: '/admin/catalogo', label: 'Catalogo', icon: Table2 },
  { to: '/admin/releases', label: 'Release', icon: Rocket },
  { to: '/admin/entitlements', label: 'Entitlements', icon: ListChecks },
];

function AdminLayout() {
  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="border-b border-zinc-200 bg-white">
          <div className="max-w-7xl mx-auto px-6 py-3 flex items-center gap-3">
            <Wand2 className="h-5 w-5 text-purple-600" />
            <span className="text-sm font-semibold text-zinc-900 mr-4">Admin DryConfig</span>
            <nav className="flex gap-1">
              {AdminTabs.map((t) => (
                <NavLink
                  key={t.to}
                  to={t.to}
                  end={t.to === '/admin'}
                  className={({ isActive }) =>
                    `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      isActive
                        ? 'bg-purple-100 text-purple-900 font-medium'
                        : 'text-zinc-600 hover:bg-zinc-100'
                    }`
                  }
                >
                  <t.icon className="h-4 w-4" />
                  {t.label}
                </NavLink>
              ))}
            </nav>
          </div>
        </div>
        <main className="p-6 max-w-7xl mx-auto w-full">
          <Outlet />
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export default function Admin() {
  return (
    <AdminGuard>
      <Routes>
        <Route element={<AdminLayout />}>
          <Route index element={<AdminOverview />} />
          <Route path="catalogo" element={<AdminCatalogo />} />
          <Route path="releases" element={<AdminReleases />} />
          <Route path="releases/:id" element={<AdminReleaseDetail />} />
          <Route path="listini" element={<AdminListini />} />
          <Route path="stratigrafie" element={<AdminStratigrafie />} />
          <Route path="entitlements" element={<AdminEntitlements />} />
          <Route path="*" element={<Navigate to="/admin" replace />} />
        </Route>
      </Routes>
    </AdminGuard>
  );
}
